
import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Printer,
  Cloud,
  School,
  Shield,
  UserSquare,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { IdCardConfig, DEFAULT_ID_CONFIG } from "./identycards/IdCardTypes";
import { IdCardRenderer } from "./identycards/IdCardRenderer";
import { StudentData, INITIAL_SUBJECTS, INITIAL_HEADER_SETTINGS } from "./identycards/types";

/* ── Props ──────────────────────────────────────────────── */
interface StudentIDCardProps {
  students: any[];     // keep any to avoid strict typing changes
  onBack: () => void;
  config?: IdCardConfig;
  schoolDetails?: { name: string; logoUrl: string; motto: string; address: string } | null;
  schoolId?: string;
}

/* ───────────────────────────────────────────────────────── */
const StudentIDCard = ({ students, onBack, config: initialConfig = DEFAULT_ID_CONFIG, schoolDetails, schoolId }: StudentIDCardProps) => {
  const [selectedStudent, setSelectedStudent] = useState<any>(students[0]);
  const [viewMode, setViewMode] = useState<"single" | "batch">("single");
  const [searchQuery, setSearchQuery] = useState("");
  const [config, setConfig] = useState<IdCardConfig>(initialConfig);
  const [configLoading, setConfigLoading] = useState(!!schoolId);

  // Sync config with Firestore real-time listener if schoolId is provided
  useEffect(() => {
    if (!schoolId) return;
    
    console.log("StudentIDCard: Subscribing to config for school", schoolId);
    setConfigLoading(true);

    const configRef = doc(db, `schools/${schoolId}/headerSettings/idCardConfig`);
    const unsubscribe = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists()) {
        const remoteConfig = docSnap.data() as IdCardConfig;
        console.log("StudentIDCard: Received remote config", remoteConfig);
        // Merge with defaults to ensure all fields exist
        setConfig({ ...DEFAULT_ID_CONFIG, ...remoteConfig });
      } else {
        console.log("StudentIDCard: No config found, using defaults");
      }
      setConfigLoading(false);
    }, (error) => {
      console.error("StudentIDCard: Error fetching config:", error);
      setConfigLoading(false);
    });

    return () => unsubscribe();
  }, [schoolId]);

  const filteredStudents = students.filter(student => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${student.firstName} ${student.middleName || ''} ${student.lastName}`.toLowerCase();
    const regNum = (student.registrationNumber || '').toLowerCase();
    return fullName.includes(searchLower) || regNum.includes(searchLower);
  });

  // Helper to map school/student data to IdCardRenderer format
  const mapToStudentData = (student: any): StudentData => {
    return {
        name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`,
        studentId: student.registrationNumber || student.id,
        gradeLevel: student.className || student.grade || "Not Assigned",
        stream: student.class || "",
        academicYear: new Date().getFullYear().toString(),
        schoolName: schoolDetails?.name || "Administration Hub",
        schoolAddress: schoolDetails?.address || "Academic Office",
        schoolContact: schoolDetails?.motto || "",
        studentUid: student.id,
        photoUrl: student.photoUrl,
        admissionDate: student.admissionDate || "",
        programDuration: 4,
        dob: student.dateOfBirth || "",
        attendancePercentage: student.attendance || 100,
        classTeacher: "Not Assigned",
        subjects: INITIAL_SUBJECTS,
        principalRemark: "",
        headerSettings: {
            ...INITIAL_HEADER_SETTINGS,
            isEnabled: true,
            logo: {
                ...INITIAL_HEADER_SETTINGS.logo,
                url: schoolDetails?.logoUrl || null,
            },
            schoolName: {
                ...INITIAL_HEADER_SETTINGS.schoolName,
                text: schoolDetails?.name || "Administration Hub",
            },
            motto: {
                ...INITIAL_HEADER_SETTINGS.motto,
                text: schoolDetails?.motto || "",
            }
        }
    };
  };

  const handlePrint = () => {
    window.print();
  };

  /* ── Batch view ───────────────────────────────────────── */
  const BatchIDCards: React.FC = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 print:flex print:flex-col print:items-center print:gap-16">
      {students.slice(0, 20).map((student) => {
        const studentData = mapToStudentData(student);
        return (
          <div
            key={student.id}
            className="flex flex-col gap-8 items-center print:break-inside-avoid"
          >
            <div className="flex flex-col items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest print:hidden">Front Side</span>
                <IdCardRenderer data={studentData} config={config} side="front" />
            </div>
            <div className="flex flex-col items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest print:hidden">Back Side</span>
                <IdCardRenderer data={studentData} config={config} side="back" />
            </div>
          </div>
        );
      })}
    </div>
  );

  /* ── Render ───────────────────────────────────────────── */
  return (
    <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 print:bg-white print:p-0">
      {/* Header (hidden in print) */}
      <div className="mb-6 print:hidden">
        <Button variant="outline" onClick={onBack} className="mb-4 flex items-center gap-2 border-slate-200 dark:border-slate-700">
          <ArrowLeft className="h-4 w-4" />
          Back to Students
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Student ID Cards
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage and batch generate identification with synchronized styles
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setViewMode(viewMode === "single" ? "batch" : "single")}
            >
              {viewMode === "single" ? "Batch View" : "Single View"}
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print Cards
            </Button>
          </div>
        </div>
      </div>

      {/* Printable area */}
      <div id="printable-cards">
        {viewMode === "single" ? (
          <div className="space-y-6">
            {/* Student selector */}
            <Card className="print:hidden">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle>Select Student</CardTitle>
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    type="search"
                    placeholder="Search name or reg number..."
                    className="pl-8 h-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-64 overflow-y-auto pt-2">
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <Button
                        key={student.id}
                        variant={selectedStudent.id === student.id ? "default" : "outline"}
                        onClick={() => setSelectedStudent(student)}
                        className="justify-start p-3 h-auto text-left"
                      >
                        <div>
                          <div className="font-medium text-sm">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-xs opacity-70">{student.registrationNumber}</div>
                        </div>
                      </Button>
                    ))
                  ) : (
                    <div className="col-span-full py-8 text-center text-gray-500">
                      No students found matching "{searchQuery}"
                    </div>
                  )}
                </div>
                {students.length > 20 && !searchQuery && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 text-center">
                    Use the search bar to find students among all records.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 justify-items-center">
              <div className="space-y-4 flex flex-col items-center">
                <div className="text-center print:hidden">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Card Front
                  </h3>
                  <Badge variant="outline" className="mb-4">Synchronized Style</Badge>
                </div>
                <IdCardRenderer data={mapToStudentData(selectedStudent)} config={config} side="front" />
              </div>

              <div className="space-y-4 flex flex-col items-center">
                <div className="text-center print:hidden">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Card Back
                  </h3>
                  <Badge variant="outline" className="mb-4">Security Features</Badge>
                </div>
                <IdCardRenderer data={mapToStudentData(selectedStudent)} config={config} side="back" />
              </div>
            </div>

            {/* Student details */}
            <Card className="print:hidden">
              <CardHeader>
                <CardTitle>Student Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Name:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      {selectedStudent.firstName} {selectedStudent.middleName}{" "}
                      {selectedStudent.lastName}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Admission Grade:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      {selectedStudent.admissionGrade || '-'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Class:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      {selectedStudent.className || selectedStudent.grade || '-'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Dormitory:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      {selectedStudent.dormitory}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Batch mode */
          <div className="space-y-6">
            <Card className="print:hidden">
              <CardHeader>
                <CardTitle>Batch ID Card Generation</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Professional ID cards for the first 20 students. Each card includes
                  synchronized design and security features.
                </p>
              </CardHeader>
            </Card>

            <BatchIDCards />
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>
        {`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-cards, #printable-cards * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            size: A4;
            margin: 0.5in;
          }
          #printable-cards {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
        }
        `}
      </style>
    </main>
  );
};

export default StudentIDCard;
